/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useMyPermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { agentsService } from '../../services/agents.service';
import { conversationsService } from '../../services/conversations.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Agent } from '../../types';
import type { Conversation } from '../../types/conversation.types';
import ConversationSidebar from './conversation-sidebar';
import ConversationThread, { buildChatUi } from './conversation-thread';

/** The full ChatGPT-style chat window: Project → Co-pilot → Chats rail + chat pane. */
const ChatWorkspace = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const toast = useRef<Toast>(null);
  const { has } = useMyPermissions();
  const canSend = has(PERMISSIONS.CONVERSATION_EXECUTE);
  const canCreate = has(PERMISSIONS.CONVERSATION_CREATE);

  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentCode, setAgentCode] = useState<string | null>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(sp.get('c'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const err = (e: any, fallback: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, fallback), life: 7000 });

  // 1) Projects + deep-link (?c=<conversationId>) resolution.
  useEffect(() => {
    projectsService.getAccessible().then(async (ps) => {
      setProjects(ps);
      const deep = sp.get('c');
      if (deep) {
        try {
          const conv = await conversationsService.getById(deep);
          setProjectId(conv.appId || ps[0]?._id || null);
          setAgentCode(conv.agentCode || null);
          setActiveId(conv._id);
          setReady(true);
          return;
        } catch { /* fall through to defaults */ }
      }
      setProjectId(ps[0]?._id ?? null);
      setReady(true);
    }).catch(() => { setProjects([]); setReady(true); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) Conversational agents for the selected project.
  useEffect(() => {
    if (!ready) return;
    let active = true;
    setLoadingAgents(true);
    const params: any = { page: 1, page_size: 200, include_global: true };
    if (projectId) params.app_id = projectId;
    agentsService.list(params)
      .then((res) => {
        if (!active) return;
        const all = res.data || [];
        const chat = all.filter((a: any) => a.conversation?.enabled);
        const list = chat.length ? chat : all;
        setAgents(list);
        setAgentCode((cur) => (cur && list.some((a) => a.agentCode === cur)) ? cur : (list[0]?.agentCode ?? null));
      })
      .catch(() => { if (active) setAgents([]); })
      .finally(() => { if (active) setLoadingAgents(false); });
    return () => { active = false; };
  }, [projectId, ready]);

  // 3) Conversations for the selected project + co-pilot.
  const loadConversations = useCallback(() => {
    if (!agentCode) { setConversations([]); return; }
    setLoadingConvos(true);
    conversationsService.list({ page: 1, pageSize: 100, appId: projectId || undefined, agentCode })
      .then((res) => setConversations(res.data || []))
      .catch(() => setConversations([]))
      .finally(() => setLoadingConvos(false));
  }, [projectId, agentCode]);
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Keep ?c= in sync with the open chat.
  useEffect(() => { if (activeId) setSp({ c: activeId }, { replace: true }); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectProject = (id: string | null) => { setProjectId(id); setAgentCode(null); setActiveId(null); };
  const onSelectAgent = (code: string) => { setAgentCode(code); setActiveId(null); };
  const onNewChat = async () => {
    if (!agentCode) return;
    try {
      const created = await conversationsService.create({ agentCode, appId: projectId || undefined });
      setConversations((prev) => [created, ...prev]);
      setActiveId(created._id);
    } catch (e) { err(e, 'Could not start a new chat'); }
  };

  const activeConv = conversations.find((c) => c._id === activeId) || null;
  const activeAgent = agents.find((a) => a.agentCode === (activeConv?.agentCode || agentCode)) || null;
  const threadAgentCode = activeConv?.agentCode || agentCode || '';

  // Per-co-pilot presentation (Phase B) from the agent, with sensible fallbacks.
  const ui = buildChatUi(activeAgent);
  const copilotTitle = ui.title || 'Co-pilot';
  const copilotIcon = ui.icon || 'pi pi-android';
  const accent = ui.accentColor || null;
  const accentStyle = accent ? { backgroundColor: `${accent}1a`, color: accent } : undefined;

  return (
    <PermissionGuard jobName={JOB_NAMES.CONVERSATIONS}>
      <Toast ref={toast} position="top-right" />
      <div className="flex h-full min-h-0 overflow-hidden" data-testid="SCR-Chat-Workspace">
        {sidebarOpen && (
          <ConversationSidebar
            projects={projects} selectedProjectId={projectId} onSelectProject={onSelectProject}
            agents={agents} selectedAgentCode={agentCode} onSelectAgent={onSelectAgent} loadingAgents={loadingAgents}
            conversations={conversations} activeConversationId={activeId} onSelectConversation={setActiveId}
            loadingConversations={loadingConvos} onNewChat={onNewChat} canCreate={canCreate}
          />
        )}

        <main className="flex min-w-0 flex-1 flex-col bg-surface">
          {/* Branded header */}
          <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <Button icon={sidebarOpen ? 'pi pi-angle-double-left' : 'pi pi-bars'} rounded text size="small"
              aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'} title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              onClick={() => setSidebarOpen((v) => !v)} />
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" style={accentStyle}>
              <i className={copilotIcon} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{copilotTitle}</div>
              <div className="truncate text-xs text-ink-muted">
                {activeConv?.title || (agentCode ? 'New chat' : 'Pick a co-pilot')}
              </div>
            </div>
            <Button icon="pi pi-list" rounded text size="small" aria-label="Conversations list"
              title="All conversations (table)" onClick={() => navigate('/conversations')} />
          </div>

          {/* Chat pane */}
          {activeId ? (
            <ConversationThread conversationId={activeId} agentCode={threadAgentCode} canSend={canSend} ui={ui} onTurnDone={loadConversations} />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <i className="pi pi-comments text-xl" aria-hidden />
              </div>
              <p className="text-sm text-ink-muted">
                {agentCode ? 'Select a chat on the left, or start a new one.' : 'Pick a co-pilot on the left to begin.'}
              </p>
              {canCreate && agentCode && (
                <Button label="New chat" icon="pi pi-plus" size="small" onClick={onNewChat} />
              )}
            </div>
          )}
        </main>
      </div>
    </PermissionGuard>
  );
};

export default ChatWorkspace;
